/**
 * Fixed-timestep game loop.
 *
 * Simulation runs at a stable rate (good for physics); rendering runs as fast
 * as the display allows, or can be capped with `maxFps` so it stays in phase
 * with the simulation. If the tab stalls, catch-up is capped so the game slows
 * down instead of locking up in a spiral of death.
 */
export class Loop {
  constructor({ update, render, fixedStep = 1 / 60, maxSubSteps = 4, maxFps = 0 }) {
    this.update = update;
    this.render = render;
    this.fixedStep = fixedStep;
    this.maxSubSteps = maxSubSteps;
    this.maxFps = maxFps;

    this.running = false;
    this.accumulator = 0;
    this.lastTime = 0;
    this.frameHandle = 0;

    // Render pacing. `renderTime` is the wall time covered since the last
    // rendered frame (used for frame-rate-independent camera/HUD updates);
    // `renderClock` spaces those frames out when maxFps is set.
    this.renderTime = 0;
    this.renderClock = 0;
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.renderTime = 0;
    this.renderClock = 0;
    this.frameHandle = requestAnimationFrame(this.#tick);
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.frameHandle);
    this.frameHandle = 0;
  }

  #tick = (now) => {
    if (!this.running) return;

    this.frameHandle = requestAnimationFrame(this.#tick);

    const frameTime = Math.min((now - this.lastTime) / 1000, 0.25);
    this.lastTime = now;
    this.accumulator += frameTime;

    let steps = 0;
    while (this.accumulator >= this.fixedStep && steps < this.maxSubSteps) {
      this.update(this.fixedStep);
      this.accumulator -= this.fixedStep;
      steps += 1;
    }

    // Drop any leftover time rather than trying to simulate a huge catch-up.
    if (steps === this.maxSubSteps) this.accumulator = 0;

    this.renderTime += frameTime;
    this.renderClock += frameTime;

    // When the cap matches the simulation rate, render only on frames that
    // actually advanced physics. This keeps every rendered frame on a fresh
    // pose instead of alternating "stale pose, then two steps at once", which
    // reads as a jitter in the vehicle even though the average speed is right.
    const stepLocked =
      this.maxFps > 0 && Math.abs(this.maxFps * this.fixedStep - 1) < 0.02;

    if (stepLocked) {
      if (steps === 0) return;
    } else if (this.maxFps > 0) {
      const interval = 1 / this.maxFps;
      // Small tolerance so a 60 Hz display is not skipped because its frame
      // arrived a fraction of a millisecond early.
      if (this.renderClock < interval - 0.001) return;
      // Keep the overshoot so renders stay evenly spaced on displays whose
      // refresh rate is not a multiple of maxFps, but do not let it build up.
      this.renderClock = Math.max(0, Math.min(this.renderClock - interval, interval));
    }

    const renderTime = this.renderTime;
    this.renderTime = 0;
    this.render(renderTime, this.accumulator / this.fixedStep);
  };
}
